import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import ShareButton from '@/components/common/ShareButton';
import { getImageUrl, handleImageError } from '@/utils/helpers';
import { DualPrice } from '@/utils/priceUtils';
import { ArrowLeft, ShoppingCart, ChevronDown, Heart, Bookmark, Palette, Tag } from 'lucide-react';
import { useCart } from '@/contexts/CartContext';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const ArtworkDetailPage = () => {
  const { artworkId } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { addToCart, loading: cartLoading } = useCart();
  const { user, token } = useAuth();
  const [artwork, setArtwork] = useState(null);
  const [relatedProduct, setRelatedProduct] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isLiked, setIsLiked] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [likesCount, setLikesCount] = useState(0);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [artworkId]);

  useEffect(() => {
    const fetchArtwork = async () => {
      try {
        const res = await axios.get(`${API}/artworks/${artworkId}`);
        setArtwork(res.data);
        setIsLiked(res.data.is_liked || false);
        setIsSaved(res.data.is_saved || false);
        setLikesCount(res.data.likes_count || 0);
        
        // Fetch related product if exists
        if (res.data.related_product_id) {
          try {
            const productRes = await axios.get(`${API}/products/${res.data.related_product_id}`);
            setRelatedProduct(productRes.data);
          } catch (err) {
            console.error('Failed to fetch related product:', err);
          }
        }
      } catch (err) {
        console.error('Failed to fetch artwork:', err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchArtwork();
  }, [artworkId]);

  // 点赞
  const handleLike = async () => {
    if (!user) {
      toast.error(t('common.loginRequired', '请先登录'));
      return;
    }
    try {
      await axios.post(`${API}/artworks/${artworkId}/like`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIsLiked(!isLiked);
      setLikesCount(prev => isLiked ? prev - 1 : prev + 1);
      toast.success(isLiked ? t('artwork.unliked', '已取消点赞') : t('artwork.liked', '已点赞'));
    } catch (err) {
      console.error('Like failed:', err);
      toast.error(t('common.error', '操作失败'));
    }
  };

  // 收藏
  const handleSave = async () => {
    if (!user) {
      toast.error(t('common.loginRequired', '请先登录'));
      return;
    }
    try {
      await axios.post(`${API}/artworks/${artworkId}/save`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIsSaved(!isSaved);
      toast.success(isSaved ? t('artwork.unsaved', '已取消收藏') : t('artwork.saved', '已收藏'));
    } catch (err) {
      console.error('Save failed:', err);
      toast.error(t('common.error', '操作失败'));
    }
  };

  // Get localized field
  const getLocalizedField = (item, field) => {
    if (!item) return '';
    const lang = i18n.language;
    if (lang === 'en' && item[`${field}_en`]) return item[`${field}_en`];
    if (lang === 'ru' && item[`${field}_ru`]) return item[`${field}_ru`];
    return item[field] || '';
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-amber-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600"></div>
      </div>
    );
  }

  if (!artwork) {
    return (
      <div className="min-h-screen bg-amber-50 flex flex-col items-center justify-center">
        <h2 className="text-2xl font-bold text-amber-800 mb-4">{t('artwork.notFound', 'Artwork not found')}</h2>
        <Button onClick={() => navigate('/')} className="bg-amber-600 hover:bg-amber-700">{t('common.backToHome', 'Back to Home')}</Button>
      </div>
    );
  }

  const allImages = [artwork.image_url, ...(artwork.images || [])].filter(Boolean).map(img => getImageUrl(img));
  
  // 获取完整的图片URL用于Open Graph
  const getFullImageUrl = (imgPath) => {
    if (!imgPath) return '';
    if (imgPath.startsWith('http')) return imgPath;
    return `${window.location.origin}${imgPath}`;
  };

  const artworkTitle = getLocalizedField(artwork, 'title');
  const artworkDescription = getLocalizedField(artwork, 'description');
  const fullImageUrl = getImageUrl(artwork.image_url);

  return (
    <div className="min-h-screen bg-amber-50 pt-16">
      {/* 动态Open Graph标签 - 用于社交媒体分享 */}
      <Helmet>
        <title>{artworkTitle} - ArtCanvas</title>
        <meta name="description" content={artworkDescription?.substring(0, 160) || ''} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={artworkTitle} />
        <meta property="og:description" content={artworkDescription?.substring(0, 200) || ''} />
        <meta property="og:image" content={fullImageUrl} />
        <meta property="og:url" content={`${window.location.origin}/artwork/${artworkId}`} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={artworkTitle} />
        <meta name="twitter:description" content={artworkDescription?.substring(0, 200) || ''} />
        <meta name="twitter:image" content={fullImageUrl} />
      </Helmet>
      
      {/* Image Section - Full artwork display */}
      <section className="bg-amber-50" data-testid="artwork-hero">
        <div className="relative w-full flex justify-center px-4 py-2">
          <img loading="lazy"
            src={allImages[currentImageIndex]}
            alt={getLocalizedField(artwork, 'title')}
            className="max-w-full md:max-w-3xl object-contain mx-auto"
            onError={handleImageError}
          />
          {/* Back Button */}
          <Button
            variant="ghost"
            className="absolute top-4 left-4 text-gray-700 bg-white/80 hover:bg-white z-10 shadow-sm"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft className="w-5 h-5 mr-2" /> {t('common.back', 'Back')}
          </Button>
        </div>
      </section>

      {/* Action Bar - Always visible below image */}
      <section className="sticky top-16 z-30 bg-white border-b border-amber-100 shadow-sm" data-testid="artwork-action-bar">
        <div className="container mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <h2 className="text-base md:text-lg font-bold text-amber-900 truncate flex-1">
            {artworkTitle}
          </h2>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={handleLike}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm transition-all ${
                isLiked 
                  ? 'bg-red-50 text-red-600 border border-red-200' 
                  : 'bg-gray-50 text-gray-600 border border-gray-200 hover:bg-gray-100'
              }`}
              data-testid="artwork-like-btn"
            >
              <Heart className={`w-4 h-4 ${isLiked ? 'fill-red-500' : ''}`} />
              <span>{likesCount}</span>
            </button>
            <button
              onClick={handleSave}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm transition-all ${
                isSaved 
                  ? 'bg-amber-50 text-amber-700 border border-amber-200' 
                  : 'bg-gray-50 text-gray-600 border border-gray-200 hover:bg-gray-100'
              }`}
              data-testid="artwork-save-btn"
            >
              <Bookmark className={`w-4 h-4 ${isSaved ? 'fill-amber-600' : ''}`} />
              <span className="hidden sm:inline">{isSaved ? t('artwork.saved', '已收藏') : t('artwork.save', '收藏')}</span>
            </button>
            <ShareButton
              url={`/artwork/${artworkId}`}
              title={artworkTitle}
              description={artworkDescription?.substring(0, 200) || ''}
              image={artwork.image_url}
              size="sm"
            />
            {(artwork?.allow_merchandise || artwork?.is_original_for_sale) && (
              <Button
                size="sm"
                className="bg-orange-500 hover:bg-orange-600 text-white rounded-full px-4"
                onClick={() => navigate(`/customize/${artworkId}${artwork?.is_original_for_sale ? '?tab=original' : ''}`)}
                data-testid="artwork-customize-btn"
              >
                <Palette className="w-4 h-4 mr-1.5" />
                <span className="hidden sm:inline">{t('artwork.purchaseOrCustomize')}</span>
                <span className="sm:hidden">{t('artwork.buy', '购买')}</span>
              </Button>
            )}
          </div>
        </div>
      </section>

      {/* Description Section - 关于此作品 */}
      <section className="py-20 bg-amber-50/50">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="text-center mb-16">
            {/* 作品标题 */}
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-amber-900 mb-4">
              {getLocalizedField(artwork, 'title')}
            </h1>
            
            {/* 作者信息 */}
            {(artwork.artist || artwork.creator_name) && (
              <p 
                className="text-xl md:text-2xl text-amber-700 mb-6 cursor-pointer hover:text-amber-600 transition-colors"
                onClick={() => artwork.creator_id && navigate(`/creator/${artwork.creator_id}`)}
              >
                {t('artwork.author', '作者')}: {getLocalizedField(artwork, 'artist') || artwork.creator_name}
              </p>
            )}
            
            <h2 className="text-2xl md:text-3xl font-semibold text-amber-800 mb-6">
              {t('artwork.aboutThisWork', 'About This Work')}
            </h2>
            <p className="text-xl text-amber-700/80 leading-relaxed">
              {getLocalizedField(artwork, 'description')}
            </p>
          </div>

          {/* Details Grid */}
          <div className="grid md:grid-cols-3 gap-8 text-center">
            {artwork.year && (
              <div>
                <p className="text-sm text-amber-600 uppercase tracking-wide mb-2">{t('artwork.year', 'Year')}</p>
                <p className="text-2xl font-semibold text-amber-900">{artwork.year}</p>
              </div>
            )}
            {artwork.medium && (
              <div>
                <p className="text-sm text-amber-600 uppercase tracking-wide mb-2">{t('artwork.medium', 'Medium')}</p>
                <p className="text-2xl font-semibold text-amber-900">{getLocalizedField(artwork, 'medium')}</p>
              </div>
            )}
            {artwork.dimensions && (
              <div>
                <p className="text-sm text-amber-600 uppercase tracking-wide mb-2">{t('artwork.dimensions', 'Dimensions')}</p>
                <p className="text-2xl font-semibold text-amber-900">{artwork.dimensions}</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Story Section */}
      {getLocalizedField(artwork, 'story') && (
        <section className="py-20 bg-amber-50">
          <div className="container mx-auto px-4 max-w-4xl">
            <h2 className="text-3xl md:text-4xl font-bold text-amber-900 mb-8 text-center">
              {t('artwork.theStory', 'The Story')}
            </h2>
            <div className="prose prose-lg mx-auto text-amber-700">
              <p className="whitespace-pre-line leading-relaxed">
                {getLocalizedField(artwork, 'story')}
              </p>
            </div>
          </div>
        </section>
      )}

      {/* Original Artwork Purchase - Redirect to CustomizePage */}
      {artwork.is_original_for_sale && artwork.original_price > 0 && (
        <section className="py-20 bg-gradient-to-b from-amber-50 to-amber-100/50" data-testid="original-purchase-section">
          <div className="container mx-auto px-4 max-w-3xl text-center">
            <Badge className="bg-red-500 text-white text-sm px-4 py-1 mb-4">
              {t('artwork.originalForSale')}
            </Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-amber-900 mb-3">
              {t('artwork.ownTheOriginal')}
            </h2>
            <p className="text-lg text-amber-700 mb-4">
              {t('artwork.originalDesc')}
            </p>
            <div className="flex items-baseline justify-center gap-2 mb-6">
              <DualPrice kwhAmount={artwork.original_price} size="xl" className="items-center" />
            </div>
            <Button
              size="lg"
              className="bg-red-600 hover:bg-red-700 py-6 px-10 text-lg rounded-xl"
              onClick={() => navigate(`/customize/${artworkId}?tab=original`)}
              data-testid="go-to-original-purchase-btn"
            >
              <Tag className="w-5 h-5 mr-2" />
              {t('artwork.buyOriginal')}
            </Button>
          </div>
        </section>
      )}

      {/* Sold Badge */}
      {artwork.is_sold && !artwork.is_original_for_sale && (
        <section className="py-12 bg-gray-50">
          <div className="container mx-auto px-4 max-w-3xl text-center">
            <Badge variant="secondary" className="text-lg px-6 py-2">
              {t('artwork.sold', '此原作已售出')}
            </Badge>
          </div>
        </section>
      )}

      {/* Video Section */}
      {artwork.video_url && (
        <section className="py-20 bg-black">
          <div className="container mx-auto px-4 max-w-5xl">
            <video
              controls
              className="w-full rounded-2xl shadow-2xl"
              poster={artwork.image_url}
            >
              <source src={artwork.video_url} type="video/mp4" />
            </video>
          </div>
        </section>
      )}

      {/* Related Product Section */}
      {relatedProduct && (
        <section className="py-20 bg-gradient-to-b from-amber-50 to-white">
          <div className="container mx-auto px-4 max-w-5xl">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-amber-900 mb-4">
                {t('artwork.ownThisArt', 'Own This Art')}
              </h2>
              <p className="text-xl text-amber-700">
                {t('artwork.printedOnBag', 'Printed on organic cotton tote bag')}
              </p>
            </div>

            <Card className="overflow-hidden shadow-xl">
              <div className="grid md:grid-cols-2">
                <div className="bg-amber-100 p-8 flex items-center justify-center">
                  <img loading="lazy"
                    src={getImageUrl(relatedProduct.image_url)}
                    alt={getLocalizedField(relatedProduct, 'name')}
                    className="max-h-96 object-contain"
                  />
                </div>
                <CardContent className="p-8 flex flex-col justify-center">
                  <h3 className="text-2xl font-bold text-amber-900 mb-4">
                    {getLocalizedField(relatedProduct, 'name')}
                  </h3>
                  <p className="text-amber-700 mb-6">
                    {getLocalizedField(relatedProduct, 'description')}
                  </p>
                  <div className="flex items-baseline gap-4 mb-8">
                    <span className="text-3xl font-bold text-green-600">
                      ¥{relatedProduct.price?.toFixed(2)}
                    </span>
                    {relatedProduct.original_price && relatedProduct.original_price > relatedProduct.price && (
                      <span className="text-lg text-amber-500 line-through">
                        ¥{relatedProduct.original_price?.toFixed(2)}
                      </span>
                    )}
                  </div>
                  <Button
                    size="lg"
                    className="bg-green-600 hover:bg-green-700 rounded-full"
                    onClick={() => addToCart(relatedProduct.id)}
                    disabled={cartLoading}
                    data-testid="add-to-cart-artwork-btn"
                  >
                    <ShoppingCart className="w-5 h-5 mr-2" />
                    {cartLoading ? t('common.loading', '加载中...') : t('products.addToCart', '加入购物车')}
                  </Button>
                </CardContent>
              </div>
            </Card>
          </div>
        </section>
      )}

      {/* Footer CTA */}
      <section className="py-16 bg-black text-white text-center">
        <div className="container mx-auto px-4">
          <h3 className="text-2xl md:text-3xl font-bold mb-6">
            {t('artwork.exploreMore', 'Explore More Artworks')}
          </h3>
          <div className="flex flex-wrap gap-4">
            <Button
              variant="outline"
              size="lg"
              className="border-white text-white hover:bg-white hover:text-black rounded-full"
              onClick={() => navigate('/artworks')}
            >
              {t('artwork.viewAllArtworks', '查看全网画作')}
            </Button>
            {artwork?.creator_id && (
              <Button
                variant="outline"
                size="lg"
                className="border-white text-white hover:bg-white hover:text-black rounded-full"
                onClick={() => navigate(`/profile/${artwork.creator_id}?tab=artworks`)}
              >
                {t('artwork.viewArtistWorks', '查看作者其它画作')}
              </Button>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default ArtworkDetailPage;
